import { useCallback, useMemo, useState, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  FormControlLabel,
} from '@mui/material';
import {
  AddRounded,
  DeleteOutlineRounded,
  ExpandMoreRounded,
  ContentCopyRounded,
} from '@mui/icons-material';
import { formatDate } from '../../../lib/i18n-format';

const CopyRubricFromProgramDialog = lazy(() =>
  import('./CopyRubricFromProgramDialog').then((m) => ({
    default: m.CopyRubricFromProgramDialog,
  })),
);

// ─── Rubric shapes (mirror @dosfilos/domain TenantRubric) ───────────────────

export interface RubricLevel {
  id: string;
  name: string;
  description: string;
}

export interface RubricCriterion {
  id: string;
  label: string;
  description: string;
  weight: number;
  levels?: RubricLevel[];
}

export interface RubricProvenance {
  programId: string;
  programName: string;
  copiedAt: string;
}

export interface Rubric {
  id: string;
  title: string;
  criteria: RubricCriterion[];
  copiedFrom?: RubricProvenance;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const uid = (): string => crypto.randomUUID();

const slugify = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const sumWeights = (criteria: RubricCriterion[]): number =>
  criteria.reduce((s, c) => s + (Number.isFinite(c.weight) ? c.weight : 0), 0);

const defaultLevels = (): RubricLevel[] => [
  { id: uid(), name: 'Ejemplar', description: '' },
  { id: uid(), name: 'Competente', description: '' },
  { id: uid(), name: 'Aceptable', description: '' },
  { id: uid(), name: 'Deficiente', description: '' },
];

// ─── Single-rubric editor ───────────────────────────────────────────────────

interface SingleRubricEditorProps {
  value: Rubric;
  onChange: (next: Rubric) => void;
  /** When true the user may rename the `id` slug; off by default to avoid
   *  breaking `AssignmentTemplate.rubricRef` references. */
  allowIdEdit?: boolean;
}

function SingleRubricEditor({
  value,
  onChange,
  allowIdEdit = false,
}: SingleRubricEditorProps): JSX.Element {
  const { t } = useTranslation('sis');
  const total = sumWeights(value.criteria);
  const weightsOk = Math.abs(total - 100) < 0.5;
  const hasLevels = value.criteria.some((c) => c.levels && c.levels.length > 0);

  const updateCriterion = (idx: number, patch: Partial<RubricCriterion>): void => {
    onChange({
      ...value,
      criteria: value.criteria.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    });
  };

  const addCriterion = (): void => {
    onChange({
      ...value,
      criteria: [...value.criteria, { id: uid(), label: '', description: '', weight: 0 }],
    });
  };

  const removeCriterion = (idx: number): void => {
    onChange({ ...value, criteria: value.criteria.filter((_, i) => i !== idx) });
  };

  const toggleLevels = (enabled: boolean): void => {
    onChange({
      ...value,
      criteria: value.criteria.map((c) =>
        enabled
          ? { ...c, levels: c.levels?.length ? c.levels : defaultLevels() }
          : (() => {
              const { levels: _drop, ...rest } = c;
              void _drop;
              return rest;
            })(),
      ),
    });
  };

  const updateLevel = (critIdx: number, levelIdx: number, patch: Partial<RubricLevel>): void => {
    onChange({
      ...value,
      criteria: value.criteria.map((c, i) =>
        i === critIdx
          ? {
              ...c,
              levels: (c.levels ?? []).map((l, j) => (j === levelIdx ? { ...l, ...patch } : l)),
            }
          : c,
      ),
    });
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          label={t('rubricEditor.rubricTitle')}
          size="small"
          fullWidth
          value={value.title}
          onChange={(e) => {
            const next: Rubric = { ...value, title: e.target.value };
            if (!allowIdEdit && (!value.id || !value.id.trim())) {
              next.id = slugify(e.target.value) || uid();
            }
            onChange(next);
          }}
        />
        <TextField
          label={t('rubricEditor.idSlug')}
          size="small"
          value={value.id}
          disabled={!allowIdEdit}
          onChange={(e) => onChange({ ...value, id: slugify(e.target.value) })}
          sx={{ width: 200 }}
          helperText={
            allowIdEdit ? t('rubricEditor.idHelperEditable') : t('rubricEditor.idHelperLocked')
          }
        />
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={hasLevels}
              onChange={(e) => toggleLevels(e.target.checked)}
            />
          }
          label={t('rubricEditor.levelsToggle')}
        />
        <Chip
          size="small"
          label={t('rubricEditor.weightSum', { total })}
          color={weightsOk ? 'success' : 'warning'}
          variant="outlined"
        />
      </Stack>

      {!weightsOk && (
        <Alert severity="warning" variant="outlined">
          {t('rubricEditor.weightWarning', { total })}
        </Alert>
      )}

      <Stack spacing={2}>
        {value.criteria.map((c, idx) => (
          <Box
            key={c.id}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
              bgcolor: 'background.default',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
              <Chip
                size="small"
                label={t('rubricEditor.criterionChip', { num: idx + 1 })}
                sx={{ fontWeight: 700 }}
              />
              <TextField
                label={t('rubricEditor.criterionLabel')}
                size="small"
                fullWidth
                value={c.label}
                onChange={(e) => updateCriterion(idx, { label: e.target.value })}
              />
              <TextField
                label={t('rubricEditor.weightLabel')}
                size="small"
                type="number"
                value={c.weight}
                onChange={(e) =>
                  updateCriterion(idx, { weight: Math.max(0, Number(e.target.value) || 0) })
                }
                sx={{ width: 110 }}
              />
              <Tooltip title={t('rubricEditor.deleteCriterion')}>
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => removeCriterion(idx)}
                  aria-label={t('rubricEditor.deleteCriterion')}
                >
                  <DeleteOutlineRounded fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            <TextField
              label={t('rubricEditor.criterionDescription')}
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={c.description}
              onChange={(e) => updateCriterion(idx, { description: e.target.value })}
            />

            {c.levels && c.levels.length > 0 && (
              <Stack spacing={1} mt={1.5}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {t('rubricEditor.levelsHeading')}
                </Typography>
                {c.levels.map((l, j) => (
                  <Stack key={l.id} direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      label={t('rubricEditor.levelName')}
                      size="small"
                      value={l.name}
                      onChange={(e) => updateLevel(idx, j, { name: e.target.value })}
                      sx={{ width: 160 }}
                    />
                    <TextField
                      label={t('rubricEditor.levelDescription')}
                      size="small"
                      fullWidth
                      multiline
                      minRows={1}
                      value={l.description}
                      onChange={(e) => updateLevel(idx, j, { description: e.target.value })}
                    />
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>
        ))}

        <Button
          startIcon={<AddRounded />}
          onClick={addCriterion}
          size="small"
          sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
        >
          {t('rubricEditor.addCriterion')}
        </Button>
      </Stack>
    </Stack>
  );
}

// ─── Multi-rubric catalog editor ────────────────────────────────────────────

interface RubricCatalogEditorProps {
  value: Rubric[];
  onChange: (next: Rubric[]) => void;
  /** Helper text shown above the list. */
  helperText?: string;
  /**
   * When provided, enables the "Copiar desde otro programa" action and
   * excludes this id from the source program picker. Leave undefined on
   * the Tenant settings page (no copy-between-tenants for now).
   */
  currentProgramId?: string;
}

export function RubricCatalogEditor({
  value,
  onChange,
  helperText,
  currentProgramId,
}: RubricCatalogEditorProps): JSX.Element {
  const { t } = useTranslation('sis');
  const [expanded, setExpanded] = useState<string | null>(value[0]?.id ?? null);
  const [copyOpen, setCopyOpen] = useState(false);

  const updateRubric = useCallback(
    (idx: number, next: Rubric) => {
      onChange(value.map((r, i) => (i === idx ? next : r)));
    },
    [value, onChange],
  );

  const addRubric = useCallback(() => {
    const next: Rubric = {
      id: uid(),
      title: '',
      criteria: [{ id: uid(), label: '', description: '', weight: 100 }],
    };
    onChange([...value, next]);
    setExpanded(next.id);
  }, [value, onChange]);

  const removeRubric = useCallback(
    (idx: number) => {
      const removed = value[idx];
      onChange(value.filter((_, i) => i !== idx));
      if (removed && expanded === removed.id) setExpanded(null);
    },
    [value, onChange, expanded],
  );

  const duplicateRubric = useCallback(
    (idx: number) => {
      const src = value[idx];
      if (!src) return;
      const copy: Rubric = {
        id: uid(),
        title: `${src.title} (copia)`,
        criteria: src.criteria.map((c) => ({
          ...c,
          id: uid(),
          ...(c.levels ? { levels: c.levels.map((l) => ({ ...l, id: uid() })) } : {}),
        })),
      };
      onChange([...value, copy]);
      setExpanded(copy.id);
    },
    [value, onChange],
  );

  const totals = useMemo(
    () => value.map((r) => ({ id: r.id, sum: sumWeights(r.criteria) })),
    [value],
  );

  return (
    <Stack spacing={2}>
      {helperText && (
        <Typography variant="caption" color="text.secondary">
          {helperText}
        </Typography>
      )}

      {value.length === 0 && (
        <Alert severity="info" variant="outlined">
          {t('rubricEditor.empty')}
        </Alert>
      )}

      <Stack spacing={1}>
        {value.map((r, idx) => {
          const total = totals.find((t) => t.id === r.id)?.sum ?? 0;
          const weightsOk = Math.abs(total - 100) < 0.5;
          return (
            <Box
              key={r.id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper',
                overflow: 'hidden',
              }}
            >
              {/* Custom header — kept OUT of AccordionSummary so action buttons
                  are not nested inside the summary's <button> (avoids the
                  validateDOMNesting warning). */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 2, py: 1.25 }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flex={1}
                  onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                  sx={{ cursor: 'pointer', minWidth: 0 }}
                >
                  <ExpandMoreRounded
                    sx={{
                      transform: expanded === r.id ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 150ms',
                      color: 'text.secondary',
                    }}
                  />
                  <Typography fontWeight={600} noWrap>
                    {r.title || `Rúbrica ${idx + 1}`}
                  </Typography>
                  <Chip size="small" label={r.id} variant="outlined" />
                  <Chip size="small" label={`${r.criteria.length} criterios`} variant="outlined" />
                  <Chip
                    size="small"
                    label={`${total}%`}
                    color={weightsOk ? 'success' : 'warning'}
                    variant="outlined"
                  />
                  {r.copiedFrom && (
                    <Tooltip
                      title={`Copiada de "${r.copiedFrom.programName}" el ${formatDate(r.copiedFrom.copiedAt)}. Misma id (slug), pero cada programa la edita independientemente — pueden diverger.`}
                    >
                      <Chip
                        size="small"
                        label={`Copiada de ${r.copiedFrom.programName}`}
                        color="info"
                        variant="outlined"
                        icon={<ContentCopyRounded sx={{ fontSize: '14px !important' }} />}
                      />
                    </Tooltip>
                  )}
                </Stack>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Duplicar">
                    <IconButton size="small" onClick={() => duplicateRubric(idx)}>
                      <ContentCopyRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar rúbrica">
                    <IconButton size="small" color="error" onClick={() => removeRubric(idx)}>
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
              {expanded === r.id && (
                <Box sx={{ px: 2, pb: 2 }}>
                  <Divider sx={{ mb: 2 }} />
                  <SingleRubricEditor value={r} onChange={(n) => updateRubric(idx, n)} />
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ alignSelf: 'flex-start', flexWrap: 'wrap' }}>
        <Button
          startIcon={<AddRounded />}
          onClick={addRubric}
          variant="outlined"
          sx={{ textTransform: 'none' }}
        >
          {t('rubricEditor.addRubric')}
        </Button>
        {currentProgramId && (
          <Button
            startIcon={<ContentCopyRounded />}
            onClick={(e) => {
              (e.currentTarget as HTMLElement).blur();
              setCopyOpen(true);
            }}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            {t('rubricEditor.copyFromProgram')}
          </Button>
        )}
      </Stack>

      {currentProgramId && copyOpen && (
        <Suspense fallback={null}>
          <CopyRubricFromProgramDialog
            open={copyOpen}
            onClose={() => setCopyOpen(false)}
            currentProgramId={currentProgramId}
            existingRubrics={value}
            onAccept={(newRubrics) => {
              onChange([...value, ...newRubrics]);
              if (newRubrics[0]) setExpanded(newRubrics[0].id);
            }}
          />
        </Suspense>
      )}
    </Stack>
  );
}
